import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Modal from '../../../components/modal/modal'
import CustomTable from '../../../components/table/table'
import { setMobileVisibleMaterialIdsAction } from '../../../actions/mobile-materials.action'
import { successSnackBar, errorSnackBar, warningSnackBar } from '../../../actions/common.action'
import { getInventoryLists } from '../../../actions/inventory.action'

/*
 * MOBILEMATERIALSMODAL.JSX - Modal to curate Mobile Visible Materials
 *
 * SUMMARY:
 * Displays a searchable, paginated materials table using the common
 * CustomTable component, with checkboxes and a visible counter (x/200).
 * Uses initial data from the parent to avoid a second fetch on open.
 * Fetches on interaction (page/search) using the same inventory list API.
 */

const MAX = 200

export default function MobileMaterialsModal({ open, onClose, onSaveSuccess, initialData = [], totalCount = 0, initialParams = {}, initialSelectedIds = [] }) {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [items, setItems] = useState(initialData)
  const [count, setCount] = useState(totalCount)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [initialSelectedSet, setInitialSelectedSet] = useState(new Set())
  const [paginationState, setPaginationState] = useState({
    search: initialParams.search || '',
    limit: Number(initialParams.limit) || 25,
    currentPage: Number(initialParams.currentPage) || 1
  })
  const isLoadingRef = useRef(false)
  const lastFetchParamsRef = useRef(null)
  const lastCallTimeRef = useRef(0)

  useEffect(() => {
    if (!open) return
    // Reset refs to allow fresh fetches
    isLoadingRef.current = false
    lastFetchParamsRef.current = null
    lastCallTimeRef.current = 0
    // 1) Set selected ids immediately so pre-checks are correct
    const initialSet = new Set(Array.isArray(initialSelectedIds) ? initialSelectedIds : [])
    setSelectedIds(initialSet)
    setInitialSelectedSet(new Set(initialSet))
    setLoaded(true)
    // 2) Use initialData passed from parent to avoid unnecessary fetch
    setItems(initialData)
    setCount(totalCount)
    setPaginationState({
      search: initialParams.search || '',
      limit: Number(initialParams.limit) || 25,
      currentPage: Number(initialParams.currentPage) || 1
    })
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Removed extra initialization fetch: selection and table seed come from parent

  async function fetchPage(nextPage, nextLimit, nextSearch, typeOverride) {
    setLoading(true)

    try {
      const offset = (nextPage - 1) * nextLimit
      const res = await dispatch(getInventoryLists({
        includeInactive: true,
        categoryId: initialParams.categoryId || '',
        categoryFilter: initialParams.categoryFilter || '',
        type: (typeOverride !== undefined ? typeOverride : (initialParams.type || '')),
        findDuplicate: false,
        offset,
        limit: nextLimit,
        search: nextSearch
      }))
      const list = res?.data?.category || res?.category || []
      const total = (res?.data?.internal || 0) + (res?.data?.external || 0) || res?.data?.totalCount || res?.totalCount || 0
      setItems(list)
      setCount(total)
    } catch (e) {
      dispatch(errorSnackBar(e?.message || 'Failed to load materials'))
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }

  function toggle(id) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id); return next }
      if (next.size >= MAX) { dispatch(errorSnackBar(`You can select at most ${MAX} materials`)); return prev }
      next.add(id); return next
    })
  }

  async function onSave() {
    setSaving(true)
    try {
      const ids = Array.from(selectedIds)

      await dispatch(setMobileVisibleMaterialIdsAction(ids))
      dispatch(successSnackBar('Mobile materials updated'))
      // Refresh parent's mobile materials list
      if (onSaveSuccess) {
        await onSaveSuccess()
      }
      onClose && onClose()
    } catch (e) {
      dispatch(errorSnackBar(e?.message || 'Failed to save'))
      dispatch(errorSnackBar(e?.message || 'Failed to save mobile materials'))
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    {
      title: '',
      field: 'selectall',
      filtering: false,
      render: (ctx) => {
        if (ctx && ctx.isHeader) {
          const pageIds = (items || []).map(r => r.id).filter(Boolean)
          const selectedCount = pageIds.filter(id => selectedIds.has(id)).length
          const allSelected = pageIds.length > 0 && selectedCount === pageIds.length
          return (
            <input
              type="checkbox"
              checked={allSelected}
              data-cy="select-all-mobile-materials"
              onChange={(e) => {
                const check = e.target.checked
                setSelectedIds(prev => {
                  const next = new Set(prev)
                  if (check) {
                    let added = 0
                    for (const id of pageIds) {
                      if (next.has(id)) continue
                      if (next.size >= MAX) break
                      next.add(id); added++
                    }
                    if (added < pageIds.length) dispatch(warningSnackBar(`Only ${added} selected on this page due to the ${MAX} limit`))
                  } else {
                    for (const id of pageIds) next.delete(id)
                  }
                  return next
                })
              }}
            />
          )
        }
        const row = ctx || {}
        return (
          <input type="checkbox" checked={selectedIds.has(row.id)} data-cy="material-checkbox" onChange={() => toggle(row.id)} />
        )
      }
    },
    { title: 'Item', field: 'item', filtering: false },
    { title: 'Part No', field: 'partNo', filtering: false },
    { title: 'Manufacturer', field: 'manufacturer', filtering: false },
    { title: 'Catalog', field: 'category.name', filtering: false, render: (row) => row?.category?.name }
  ]

  const onPageChange = (pageNumber, newLimit) => {
    const validPage = pageNumber > 0 ? pageNumber : 1
    // If limit is provided (from CustomTable's useEffect), update both
    if (newLimit && newLimit !== paginationState.limit) {
      setPaginationState(prev => ({ ...prev, limit: newLimit, currentPage: validPage }))
    } else {
      setPaginationState(prev => ({ ...prev, currentPage: validPage }))
    }
  }

  const handleLimitChange = (newLimit) => {
  }

  const onSearchChange = (val) => {
    const trimmed = String(val || '').trimStart()
    setPaginationState(prev => ({ ...prev, search: trimmed, currentPage: 1 }))
  }

  // useEffect to handle fetching when state changes, with deduplication
  useEffect(() => {
    if (!open || !loaded) return

    const currentParams = {
      page: paginationState.currentPage,
      limit: paginationState.limit,
      search: paginationState.search
    }

    const now = Date.now()

    if (isLoadingRef.current) {
      return
    }

    if (now - lastCallTimeRef.current < 100) {
      return
    }

    if (lastFetchParamsRef.current &&
      JSON.stringify(lastFetchParamsRef.current) === JSON.stringify(currentParams)) {
      return
    }

    isLoadingRef.current = true
    lastFetchParamsRef.current = currentParams

    fetchPage(paginationState.currentPage, paginationState.limit, paginationState.search).finally(() => {
      lastCallTimeRef.current = Date.now()
    })
  }, [paginationState, open, loaded])

  // Check if any changes were made
  const hasChanges = selectedIds.size !== initialSelectedSet.size ||
    Array.from(selectedIds).some(id => !initialSelectedSet.has(id)) ||
    Array.from(initialSelectedSet).some(id => !selectedIds.has(id))

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalHeading={`Mobile Materials ${loaded ? selectedIds.size : '…'}/${MAX}`}
      modalSaveTxt={saving ? 'Saving…' : 'Save'}
      onClick={hasChanges ? onSave : undefined}
      modalCloseTxt="Close"
      onEdit={hasChanges ? onSave : undefined}
      modalSaveTxtDisabledClass={hasChanges ? '' : 'disabled-btn'}
      showActionBtn
      fullWidth
      maxWidth='lg'
      data-cy="mobile-materials-modal"
    >
      <CustomTable
        columns={columns}
        data={items}
        isEdit={false}
        fileName="Mobile Materials"
        paginationCount={count}
        pageHandler={onPageChange}
        itemsPerPage={paginationState.limit}
        searchHandler={onSearchChange}
        currentPage={paginationState.currentPage}
        limitChangeHandler={handleLimitChange}
        isLoading={loading}
        externalSearchValue={paginationState.search}
        externalSearchHandler={onSearchChange}
      />
    </Modal>
  )
}


